/*
 * Premiom OI — bascule des tableaux de caractéristiques selon la variante.
 *
 * Les panneaux de toutes les variantes sont rendus côté serveur par
 * snippets/premiom-product-specs.liquid. Ce script ne fait que montrer le bon.
 *
 * Il déduit la variante des options cochées dans `.variant-selects` plutôt que de lire
 * `input[name="id"]` : cette valeur n'est mise à jour qu'après la requête de la Section
 * Rendering API, alors que les options, elles, sont à jour dès l'événement `change`.
 * C'est aussi ce qui permet de rester indépendant de l'id de la section produit, que
 * cette section-ci ne connaît pas.
 */
(() => {
  const root = document.querySelector('[data-premiom-specs]');
  if (!root) return;

  let variants = [];
  try {
    variants = JSON.parse(root.getAttribute('data-variants') || '[]');
  } catch (e) {
    return;
  }
  if (!Array.isArray(variants) || !variants.length) return;

  const panes = root.querySelectorAll('[data-variant-pane]');
  if (!panes.length) return;

  const show = (variantId) => {
    panes.forEach((pane) => {
      pane.hidden = pane.dataset.variantPane !== String(variantId);
    });
  };

  const selectedOptions = (container) => {
    const groups = container.querySelectorAll('fieldset[data-option-index], [data-option-index]');
    if (!groups.length) return null;

    const values = [];
    groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      if (Number.isNaN(index)) return;
      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      if (checked) values[index] = checked.value;
      else if (select) values[index] = select.value;
    });

    return values.some((value) => value === undefined) ? null : values;
  };

  const sync = () => {
    const container = document.querySelector('.variant-selects');
    if (!container) return;

    const values = selectedOptions(container);
    if (!values) return;

    const match = variants.find(
      (variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === values.length &&
        variant.options.every((option, index) => option === values[index])
    );

    if (match) show(match.id);
  };

  document.addEventListener('change', (event) => {
    if (event.target.closest && event.target.closest('.variant-selects')) sync();
  });
})();

/*
 * Tableaux du RTE marchand : conteneur scrollable.
 *
 * Le contenu vient de l'éditeur Shopify, on ne maîtrise donc ni le nombre de
 * colonnes ni le markup. Un tableau de composition analytique large poussait la
 * page entière en scroll horizontal sur mobile ; on l'isole dans son propre
 * conteneur. IIFE séparée de celle ci-dessus : elle doit s'exécuter même sur un
 * produit sans tableau de caractéristiques par variante.
 *
 * tabindex + role : un conteneur scrollable doit être atteignable au clavier,
 * sinon son contenu est inaccessible sans souris ni geste tactile.
 */
(() => {
  document.querySelectorAll('.premiom-product-details__body table').forEach((table) => {
    if (table.parentElement?.classList.contains('premiom-product-details__table-scroll')) return;

    const scroller = document.createElement('div');
    scroller.className = 'premiom-product-details__table-scroll';
    scroller.tabIndex = 0;
    scroller.setAttribute('role', 'region');

    const caption = table.querySelector('caption')?.textContent?.trim();
    scroller.setAttribute('aria-label', caption || 'Tableau, défilement horizontal');

    table.parentNode.insertBefore(scroller, table);
    scroller.appendChild(table);
  });
})();

/*
 * Description longue de la colonne produit : troncature et lien vers l'accordéon.
 *
 * La description est limitée à 5 lignes en CSS (assets/premiom-product.css). Ici :
 * - .is-full quand elle tient dans ces 5 lignes (plus de fondu, lien masqué) ;
 * - le lien ouvre l'accordéon « Description » puis y défile en douceur.
 * Le conteneur est réécrit (innerHTML) par assets/variant-select.js à chaque
 * changement de variante : on remesure via un MutationObserver sur son conteneur.
 */
(() => {
  const target = document.getElementById('premiom-product-description');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const measure = () => {
    document.querySelectorAll('[data-premiom-description]').forEach((description) => {
      description.classList.remove('is-full');
      description.classList.toggle('is-full', description.scrollHeight <= description.clientHeight + 1);
    });
    // Sans accordéon cible, le lien ne mènerait nulle part.
    if (!target) {
      document.querySelectorAll('[data-premiom-description-link]').forEach((link) => {
        link.hidden = true;
      });
    }
  };

  const open = (smooth) => {
    if (!target) return;
    target.open = true;
    target.scrollIntoView({ behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto', block: 'start' });
    target.querySelector('summary')?.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-premiom-description-link]');
    if (!link || !target) return;
    event.preventDefault();
    history.replaceState(null, '', '#premiom-product-description');
    open(true);
  });

  if (location.hash === '#premiom-product-description') open(false);

  measure();
  window.addEventListener('resize', measure, { passive: true });
  document.fonts?.ready.then(measure);

  const root = document.querySelector('[data-premiom-description-root]');
  if (root) new MutationObserver(measure).observe(root, { childList: true });
})();
